import React, { FC, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import SubmitButton from '../../components/submit-button/submit-button';

// Containers
import { ActivityFormInputs, ActivityRecipients, useActivityForm } from './activity-form';

// Helpers
import {
  getActivityData,
  createSetModalContent,
  deleteActivity,
  buildActivityRequest,
} from '../../helpers/activity-helper';
import { recipientsToStudentIds } from '../../helpers/student-uuid-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ActivityService from '../../services/activity-service';
import FileStorageService from '../../services/file-storage-service';

const ActivityEdit: FC<{
  activity: ActivityDto;
  classroomList: ClassroomStudents[];
  activityService: ActivityService;
  fileStorageService: FileStorageService;
}> = ({ activity, classroomList, activityService, fileStorageService }) => {
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const { activityId } = useParams<{ activityId: string }>();

  const form = useActivityForm(() => {
    const initialData = getActivityData(activity);
    const { classroomIds, studentIds, unmatchedStudentUuids } = recipientsToStudentIds(
      activity.recipients,
      classroomList
    );

    return {
      ...initialData,
      classData: classroomIds,
      studentData: studentIds,
      unmatchedStudentUuids,
    };
  });

  const save = (scheduleDate: string | null) =>
    form
      .submitOnce(async (current) => {
        await activityService.updateActivity(
          activityId,
          buildActivityRequest({ ...current, scheduleDate }, classroomList)
        );
        setToastContent({
          isOpen: true,
          text: scheduleDate ? 'Atividade agendada!' : 'Atividade enviada!',
          type: 'success',
        });
        navigate('/atividades/listar');
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao salvar a atividade.',
          type: 'error',
        });
      });

  return (
    <BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.right}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <ActivityFormInputs form={form} activity={activity} fileStorageService={fileStorageService} />
      <ActivityRecipients form={form} classroomList={classroomList} />
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <Link to='/atividades/listar'>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='deletar'
          variant='terciary'
          onClick={() =>
            deleteActivity(
              activityService,
              activityId,
              setModalContent,
              setToastContent,
              navigate
            )
          }
        />
        <SubmitButton
          value='reagendar envio'
          variant='secondary'
          onClick={() =>
            createSetModalContent(
              form.data,
              (submitData) => save(submitData.scheduleDate),
              form.setData,
              setModalContent
            )
          }
          disabled={!form.allowSubmit}
        />
        <SubmitButton
          value='enviar'
          variant='primary'
          onClick={() => save(null)}
          disabled={!form.allowSubmit}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default ActivityEdit;
